<template>
    <v-dialog :value="value" max-width="720px" persistent @input="$emit('input', $event)">
        <v-card>
            <v-card-title>
                <v-icon large class="mr-2 error--text">
                    mdi-alert-circle-outline
                </v-icon>
                Remover lista de credores
            </v-card-title>

            <v-card-text class="pt-4 black--text">
                <div v-if="previewLoading" class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" />
                    <div class="mt-3">Verificando os créditos da lista...</div>
                </div>

                <template v-else-if="removalPreview">
                    <p>
                        A lista <strong>{{ list ? list.name : '' }}</strong> será removida. Os
                        créditos mantidos exclusivamente por ela serão removidos ou restaurados ao estado anterior.
                    </p>

                    <v-alert v-if="removalPreview.mode === 'legacy'" type="warning" outlined dense>
                        Esta é uma lista antiga. Os créditos serão identificados pelo arquivo original,
                        considerando caso, empresa, CPF/CNPJ, classe, moeda e versão.
                    </v-alert>

                    <v-row dense class="mb-2">
                        <v-col cols="6" md="3">
                            <strong>Localizados</strong><br>{{ removalPreview.summary.matched }}
                        </v-col>
                        <v-col cols="6" md="3">
                            <strong>Removíveis</strong><br>{{ removalPreview.summary.removable }}
                        </v-col>
                        <v-col cols="6" md="3">
                            <strong>Bloqueados</strong><br>{{ removalPreview.summary.blocked }}
                        </v-col>
                        <v-col cols="6" md="3">
                            <strong>Não encontrados</strong><br>{{ removalPreview.summary.not_found }}
                        </v-col>
                    </v-row>

                    <v-alert v-if="removalPreview.summary.retained" type="info" outlined dense>
                        {{ removalPreview.summary.retained }} crédito(s) receberam uma importação posterior e
                        serão preservados com os dados mais recentes.
                    </v-alert>

                    <v-alert v-if="removalPreview.warnings && removalPreview.warnings.length"
                        type="warning" outlined dense>
                        {{ removalPreview.warnings.length }} crédito(s) foram alterados após a importação.
                        Como pertencem a esta lista, serão removidos ou restaurados ao estado anterior.
                    </v-alert>

                    <v-simple-table v-if="removalPreview.warnings && removalPreview.warnings.length" dense
                        class="mb-4">
                        <thead>
                            <tr>
                                <th>Credor alterado</th>
                                <th>CPF/CNPJ</th>
                                <th>Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="warning in removalPreview.warnings" :key="`warning-${warning.creditor_id}`">
                                <td>{{ warning.name || `#${warning.creditor_id}` }}</td>
                                <td>{{ warning.registration || '-' }}</td>
                                <td>{{ warning.reasons.join('; ') }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <v-alert v-if="removalPreview.blockers && removalPreview.blockers.length"
                        type="error" outlined>
                        A remoção não pode ser concluída enquanto existirem os impedimentos abaixo.
                    </v-alert>

                    <v-simple-table v-if="removalPreview.blockers && removalPreview.blockers.length" dense>
                        <thead>
                            <tr>
                                <th>Credor</th>
                                <th>CPF/CNPJ</th>
                                <th>Motivo</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="blocker in removalPreview.blockers" :key="blocker.creditor_id">
                                <td>{{ blocker.name || `#${blocker.creditor_id}` }}</td>
                                <td>{{ blocker.registration || '-' }}</td>
                                <td>{{ blocker.reasons.join('; ') }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <v-alert v-else type="error" outlined dense class="mt-4">
                        Esta ação é irreversível.
                    </v-alert>
                </template>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn text @click="close" :disabled="removing || previewLoading">
                    Cancelar
                </v-btn>
                <v-btn v-if="$allowed('delete')" color="red darken-1" text @click="confirm" :loading="removing"
                    :disabled="previewLoading || !removalPreview || !removalPreview.can_remove">
                    Remover lista
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";

// Remocao de uma lista de credores importada: previa (localizados, removiveis,
// bloqueados) e confirmacao. Usado na pagina da importacao e no QGC.
export default {
    name: "CreditorListRemoveDialog",

    props: {
        value: { type: Boolean, default: false },
        // { id, name } da lista a remover.
        list: { type: Object, default: null },
    },

    data: () => ({ previewLoading: false, removing: false, removalPreview: null }),

    watch: {
        value(open) {
            if (open) this.loadPreview();
        },
    },

    methods: {
        async loadPreview() {
            if (!this.list) return;
            this.removalPreview = null;
            this.previewLoading = true;
            try {
                const result = await axios.get(`/api/admin/creditor-lists/${this.list.id}/removal-preview`);
                this.removalPreview = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || "Erro ao verificar a remoção da lista", { position: "top-right", timeout: 5000 });
                this.close();
            } finally {
                this.previewLoading = false;
            }
        },

        close() {
            this.removalPreview = null;
            this.$emit("input", false);
        },

        async confirm() {
            if (!this.list || !this.removalPreview || !this.removalPreview.can_remove) return;
            this.removing = true;
            try {
                const result = await axios.delete(`/api/admin/creditor-lists/${this.list.id}`);
                const content = result.data.content || {};
                this.$dialog.notify.info(content.storage_cleanup_warning || "Lista e dados importados removidos com sucesso", { position: "top-right", timeout: 4000 });
                this.close();
                this.$emit("removed", this.list);
            } catch (error) {
                const content = error.response && error.response.data && error.response.data.content;
                if (error.response && error.response.status === 409 && content) {
                    this.removalPreview = {
                        mode: content.mode,
                        can_remove: false,
                        summary: content.summary,
                        blockers: content.blockers || [],
                        warnings: content.warnings || [],
                    };
                }
                this.$dialog.notify.error(this.$codes(error) || "Erro ao excluir", { position: "top-right", timeout: 2000 });
            } finally {
                this.removing = false;
            }
        },
    },
};
</script>
